import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // Optional: for GitHub-flavored markdown (e.g., links, tables, etc.)
import './ChatBubble.css';
import config from "./config.json";

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [language, setLanguage] = useState("fa");
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [iconAnimation, setIconAnimation] = useState('');
  const animations = [ 'animate-spin-yay', 'animate-wiggle-scale'];
  const emojis = ['✨', '🌟', '💫', '🎉', '⭐'];
  const [sparkles, setSparkles] = useState([]);
  const bottomRef = useRef(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [isLauncher, setIsLauncher] = useState(false);
  const queryParams = new URLSearchParams(window.location.search);
  const launcherMode = queryParams.get("chatlauncher") === "true";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const inputRef = useRef(null); // Ref for the input field
  const suppress = queryParams.get("source") === "chatbot" &&
    (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

  //if (suppress) return null; //defensive mechanism to suppress the chatbot when opened in a link inside PWA mode

  useEffect(() => {
    const handler = (e) => {
//      e.preventDefault(); // Prevent auto prompt
      setDeferredPrompt(e); // Save the event for later
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);


  useEffect(() => {     //put the cursor in the input field when the chat is opened
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {     //put the cursor in the input field when new messages are added or typing state changes
  if (!typing && inputRef.current) {
    inputRef.current.focus();
  }
}, [messages, typing]);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const isAutoStart = queryParams.get("chatautostart") === "true";
    const isAutoFull = queryParams.get("chatautofull") === "true";
    const shouldFullscreen = isStandalone || launcherMode;

    if (isAutoStart || shouldFullscreen || isAutoFull)
      setIsOpen(true);
    if (isAutoFull) setFullscreen(true);
    if (shouldFullscreen) {
      setFullscreen(true);
      setIsPWA(true);
    }
  }, []);

  useEffect(() => {
    const cached = localStorage.getItem("chat_messages");
    if (cached) {
      try {
        setMessages(JSON.parse(cached));
      } catch {}
    }

  const loadHistory = async () => {
    try {
        const res = await axios.get(`${config.apiBaseUrl}/api/chat/history/`, {
          withCredentials: true
      });
        const formatted = res.data.messages.map(m => ({
        from: m.type === "HumanMessage" ? "user" : "bot",
          text: m.content
      }));
      setMessages(formatted);
      localStorage.setItem("chat_messages", JSON.stringify(formatted));
    } catch (err) {
      console.warn("Failed to load chat history:", err);
    } finally {
      setLoading(false);
    }
    }
    loadHistory();
  }, []);


  useEffect(() => {
    if (isOpen) return;

    let timeoutId;

    const triggerAnimation = () => {
      const randomAnim = animations[Math.floor(Math.random() * animations.length)];
      const emoji = emojis[Math.floor(Math.random() * emojis.length)];
      const id = Date.now();

      const sparkle = {
        id,
        emoji,
        left: Math.random() * 40 + 10,
        top: Math.random() * 40 + 10,
      };

      setIconAnimation(randomAnim);
      setSparkles(prev => [...prev, sparkle]);

      setTimeout(() => {
        setIconAnimation('');
        setSparkles(prev => prev.filter(s => s.id !== id));
      }, 1000);

      // Random delay between 5-15 seconds
      const nextDelay = Math.random() * 10000 + 5000;
      timeoutId = setTimeout(triggerAnimation, nextDelay);
    };

    // Start first animation
    triggerAnimation();

    return () => clearTimeout(timeoutId);
  }, [isOpen]);


    const handleResize = () => {
      const widget = document.querySelector('.chat-widget.fullscreen');
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (widget && window.visualViewport && isMobile) {
      const keyboardHeight = window.innerHeight - window.visualViewport.height;

      // iOS Safari quirk: innerHeight doesn't shrink, so we rely on visualViewport directly
        widget.style.height = `${window.visualViewport.height}px`;

      // Optional: add padding to push up input
      if (keyboardHeight > 150) {
        widget.style.paddingBottom = `${keyboardHeight}px`;
        document.body.style.overflow = 'hidden'; // 🚫 Disable scrolling
      } else {
        widget.style.paddingBottom = '0px';
        document.body.style.overflow = ''; // ✅ Restore scroll
      }
    }
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!window.visualViewport) return;

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);

  return () => {
      window.visualViewport.removeEventListener('resize', handleResize);
      window.visualViewport.removeEventListener('scroll', handleResize);
  };
}, []);

    useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    handleResize(); 
  }, [messages, typing]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const isMobile = window.innerWidth <= 768;

    // Step 1: blur only on mobile to close keyboard
    if (isMobile) {
      document.activeElement?.blur();
    }

    const newMessages = [...messages, { from: 'user', text: input }];
    setMessages(newMessages);
    setInput("");
    setTyping(true);

    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/chat/`, {
          message: input,
          lang: language,
      }, {
        withCredentials: true
      });

      const updated = [...newMessages, { from: 'bot', text: res.data.response }];
        setMessages(updated);
        localStorage.setItem("chat_messages", JSON.stringify(updated));

      // Step 2: wait briefly before scrolling
      if (isMobile) {
        setTimeout(() => {
          handleResize?.();
          bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }

    } catch (error) {
      console.warn("Failed to send message:", error);
    } finally {
      setTyping(false);
    }
  };

  const resetChat = async () => {
    setResetting(true); // Start resetting
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/chat/reset/`, {}, {
        withCredentials: true
      });

      if (res.data.status === "ok") {
        localStorage.removeItem("chat_messages");
        setMessages([]);
      } else {
        console.warn("Error resetting chat.");
      }
    } catch (err) {
      console.warn("Error resetting chat:", err);
    } finally {
      setResetting(false); // End resetting
      setShowConfirm(false); // Close confirmation modal
    }
  };

  return (
    <div className={`chat-widget-wrapper ${language === 'fa' ? 'rtl' : ''}`}>
      {isOpen && (
        <div className={`chat-widget ${fullscreen ? 'fullscreen' : ''}`}>    
          <div className={`chat-header ${language === 'fa' ? 'rtl' : ''}`}>
            <div className="chat-controls">
              {language === 'fa' ? (
                <>
                  {!isLauncher && !isPWA && (
                    <>
                    <button
                      className="install-button"
                      title="اپلیکیشن"
                      onClick={async () => {
                        if (deferredPrompt) {
                          deferredPrompt.prompt(); // Show browser install prompt
                          const result = await deferredPrompt.userChoice;
                          console.log("User response to install prompt:", result.outcome);
                          setDeferredPrompt(null); // Clear it after use
                        } else {
                          window.open(config.appBaseUrl + '?chatautofull=true', '_blank', "width=700,height=700,resizable=yes");
                        }
                      }}
                    >
                      {deferredPrompt ? '📥' : '📲'}
                    </button>
                    </>
                  )}
                  <button onClick={() => setShowConfirm(true)}>↺</button>
                  <button className="language-toggle" onClick={() => setLanguage(prev => prev === 'fa' ? 'en' : 'fa')}>
                    {language === 'fa' ? 'EN' : 'FA'}
                  </button>
                  {!isPWA && (
                    <>
                  <button onClick={() => setFullscreen(!fullscreen)}>
                    {fullscreen ? "🗕" : "⤡"}
                  </button>
                  <button onClick={() => setIsOpen(false)}>×</button>
                </>
                  )}
                </>
              ) : (
                <>
                  {!isPWA && (
                    <>
                  <button onClick={() => setIsOpen(false)}>×</button>
                  <button onClick={() => setFullscreen(!fullscreen)}>
                    {fullscreen ? "🗕" : "⤡"}
                  </button>
                    </>
                  )}
                  <button className="language-toggle" onClick={() => setLanguage(prev => prev === 'fa' ? 'en' : 'fa')}>
                    {language === 'fa' ? 'EN' : 'FA'}
                  </button>
                  <button onClick={() => setShowConfirm(true)}>↺</button>
                  {!isLauncher && !isPWA && (
                    <button
                      className="install-button"
                      title="App"
                      onClick={async () => {
                        if (deferredPrompt) {
                          deferredPrompt.prompt(); // Show browser install prompt
                          const result = await deferredPrompt.userChoice;
                          console.log("User response to install prompt:", result.outcome);
                          setDeferredPrompt(null); // Clear it after use
                        } else {
                          window.open(config.appBaseUrl + '?chatautofull=true', '_blank', "width=700,height=700,resizable=yes");
                        }
                      }}
                    >
                      {deferredPrompt ? '📥' : '📲'}
                    </button>
                  )}
                </>
              )}
            </div>

            <button className="about" onClick={() => setShowInfoModal(!showInfoModal)}>
              {language === 'fa' ? '💬' : '💬'}
            </button>
          </div>
          <div className="chat-messages">
            {resetting ? (
              <div className="loading">{language === 'fa' ? 'در حال ریست...' : 'Resetting...'}</div>
            ) : (
              <>
                {Array.isArray(messages) && messages.length > 0 ? (
                  messages.map((msg, i) => {
                    const from = msg?.from === 'user' ? 'user' : 'bot'; // fallback to 'bot'
                    const text = typeof msg?.text === 'string' ? msg.text : "";

                    return (
                      <div key={i} className={`message ${from}`}>
                        {from === 'bot' ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ href, children, ...props }) => {
                                const isInternal = href.includes(config.mainSiteDomain);
                                const modifiedHref = isInternal
                                  ? (href.includes('?') ? `${href}&source=chatbot` : `${href}?source=chatbot`)
                                  : href;

                                return (
                                  <a
                                    href={modifiedHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...props}
                                  >
                                  {children}
                                </a>
                                );
                              },
                            }}
                          >
                            {text}
                          </ReactMarkdown>
                        ) : (
                          text
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="empty">
                    <ReactMarkdown>
                      {language === "fa" ? config.emptyMessage.fa : config.emptyMessage.en}
                    </ReactMarkdown>
                  </div>
                )}
                {typing && (
                  <div className="message bot typing">
                    {language === 'fa' ? '...' : '...'}
                  </div>
                )}
              </>
            )}
            <div ref={bottomRef} />
          </div>

          {showConfirm && (
            <div className="modal-overlay-body-only">
              <div className="modal-box">
                <h3>{language === 'fa' ? "پایان گفتگو" : "End Chat Session"}</h3>
                <p>
                  {language === 'fa'
                    ? "آیا مطمئن هستید که می‌خواهید گفتگو را ریست کنید؟ این کار تمام گفتگو را پاک می‌کند."
                    : "Are you sure you want to reset this chat session? This will clear the conversation."}
                </p>
                <div className="modal-actions">
                  <button className="modal-button confirm" onClick={resetChat}>
                  <span>{language === 'fa' ? 'بله' : 'Yes'}</span> 👍
                  </button>
                  <button className="modal-button cancel" onClick={() => setShowConfirm(false)}>
                  <span>{language === 'fa' ? 'خیر' : 'No'}</span> 👎
                  </button>
                </div>
              </div>
            </div>
          )}

          {showInfoModal && (
            <div className="modal-overlay-body-only">
              <div className="modal-box">
                <h3
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    marginBottom: "0.5em",
                  }}
                >
                    {language === "fa" ? config.infoModal.title.fa : config.infoModal.title.en}
                </h3>
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "1rem",
                    marginBottom: "1em",
                  }}
                >
                    {language === "fa" ? config.infoModal.version.fa : config.infoModal.version.en}
                </p>
                <p
                  style={{
                    textAlign: "justify",
                    fontSize: "0.65rem",
                    lineHeight: "1.7em",
                  }}
                >
                  {language === "fa" ? config.infoModal.content.fa : config.infoModal.content.en}
                </p>
                <div className="modal-actions">
                  <button
                    className="modal-button confirm"
                    onClick={() => {
                      setShowInfoModal(false);
                      setIsOpen(true);
                    }}
                  >
                    👍 <span>{language === "fa" ? "باشه" : "Okay"}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <input
            type="text"
            ref={inputRef} // Attach the ref here
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder={language === 'fa' ? "اینجا بنویسید ..." : "Ask something..."}
          />
        </div>
      )}

      <div
        className={`chat-toggle-button ${!isOpen ? iconAnimation : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        💬
        {!isOpen && sparkles.map(s => (
          <div
            key={s.id}
            className="sparkle"
            style={{ left: `${s.left}px`, top: `${s.top}px` }}
          >
            {s.emoji}
          </div>
        ))}
      </div>

    </div>
  );
};

export default ChatBubble;
